import React, { useState } from "react";
import { StepperHeader } from "./StepperHeader";
import { StepNavigation } from "./StepNavigation";
import InfoBox from "newui/components/InfoBox";
import type { StepProps } from "./Step";

interface StepContainerProps {
	children: React.ReactNode;
	initialStep?: number;
	onComplete?: () => void;
}

export const StepContainer: React.FC<StepContainerProps> = ({
	children,
	onComplete,
	initialStep = 0,
}) => {
	const [currentStep, setCurrentStep] = useState(initialStep);

	const steps = React.Children.toArray(children).filter(
		(child): child is React.ReactElement<StepProps> =>
			React.isValidElement(child)
	);

	const stepMeta = steps.map((step) => ({
		name: step.props.name,
		description: step.props.description,
		enabled: step.props.enabled,
		info: step.props.info,
	}));

	const activeStep = steps[currentStep];
	const currentStepEnabled = stepMeta[currentStep]?.enabled ?? false;
	const currentInfo = stepMeta[currentStep]?.info;

	const handleBack = (): void => {
		if (currentStep > 0) {
			setCurrentStep(currentStep - 1);
		}
	};

	const handleNext = (): void => {
		if (currentStep < steps.length - 1 && currentStepEnabled) {
			setCurrentStep(currentStep + 1);
		}
	};

	const handleComplete = (): void => {
		if (onComplete) {
			onComplete();
		}
	};

	return (
		<div>
			<StepperHeader
				steps={stepMeta}
				currentStep={currentStep}
			/>
			{currentInfo && (
				<div className="mb-4">
					<InfoBox>{currentInfo}</InfoBox>
				</div>
			)}
			<div className="py-2">{activeStep}</div>
			<StepNavigation
				currentStep={currentStep}
				totalSteps={steps.length}
				currentStepEnabled={currentStepEnabled}
				onBack={handleBack}
				onNext={handleNext}
				onComplete={handleComplete}
			/>
		</div>
	);
};
